/**
 * VoiceCaptureControl — mic chip + consent handshake.
 *
 * Recording is a TWO-SIDED decision between Guide and Conflict (playerB):
 * either side taps the mic to REQUEST recording; the other side gets an
 * Accept / Decline prompt in-chip; only when both consent does the local
 * `useVoiceCapture` hook start capturing audio. Either side can stop at any
 * time, which releases both mics.
 *
 * The source-of-truth lives in room state:
 *   recordingRequestedBy : 'guide' | 'playerB' | null
 *   recordingActive      : boolean
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Mic, MicOff, Loader2, Check, X } from 'lucide-react';
import { OL } from './OperationListenParts';
import useVoiceCapture from '../hooks/useVoiceCapture';

const PARTNER_LABEL = { guide: 'Guide', playerB: 'Conflict' };

export const VoiceCaptureControl = ({
  actor,            // 'guide' | 'playerB'
  roomCode,
  isDemoRoom = false,
  testId,
  activeRound = true,
  roomState = {},   // room-level game state (sole source of recording consent)
  writeState,       // (patch) => Promise — writes room state
}) => {
  const requester = roomState?.recordingRequestedBy || null;
  const active    = !!roomState?.recordingActive;
  // Only Guide + Conflict (playerB) participate in the handshake. Coach / CC
  // never see this control (nothing is passed through to them).
  const isGuide    = actor === 'guide';
  const isPlayerB  = actor === 'playerB';
  const myField    = isGuide || isPlayerB;
  const requestedByMe   = requester === actor;
  const requestedByThem = requester && requester !== actor;
  const partnerLabel = PARTNER_LABEL[requester === 'guide' ? 'guide' : 'playerB'];

  // Local hook — only actually captures audio when `active` is true.
  const { state: capState, start, stop, requestMic, error } =
    useVoiceCapture({ actor, roomCode, isDemoRoom, activeRound });
  const [busy, setBusy] = useState(false);

  // Drive the local recorder off room-state `recordingActive`.
  useEffect(() => {
    (async () => {
      if (!myField) return;
      if (active && capState !== 'recording') {
        try {
          setBusy(true);
          const ok = await requestMic();
          if (ok) await start();
        } finally { setBusy(false); }
      } else if (!active && capState === 'recording') {
        try { setBusy(true); await stop(); } finally { setBusy(false); }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, myField]);

  // Clear a stale consent request if the round ends.
  useEffect(() => {
    if (!activeRound && (requester || active) && writeState) {
      writeState({ recordingRequestedBy: null, recordingActive: false }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRound]);

  // --- actions ---
  const sendPatch = useCallback(async (patch) => {
    if (!writeState) return;
    try { setBusy(true); await writeState(patch); } finally { setBusy(false); }
  }, [writeState]);

  const requestRecord = () => sendPatch({ recordingRequestedBy: actor });
  const cancelRequest = () => sendPatch({ recordingRequestedBy: null });
  const acceptRecord  = () => sendPatch({ recordingActive: true });
  const declineRecord = () => sendPatch({ recordingRequestedBy: null });
  const stopRecord    = () => sendPatch({ recordingRequestedBy: null, recordingActive: false });

  // ------------------------------------------------------------------
  // Render — four states: idle | requested-by-me | requested-by-them | active.
  // ------------------------------------------------------------------
  const Spinner = () => <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />;

  // 1. Active — both sides consented. Red "stop" chip.
  if (active) {
    return (
      <>
        <button
          onClick={stopRecord}
          data-testid={testId}
          data-state="recording"
          disabled={busy || capState === 'denied' || capState === 'unsupported'}
          title={error || 'Recording — tap to stop'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 999,
            fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            background: 'rgba(155,37,37,0.14)', border: `1px solid ${OL.danger}`,
            color: OL.danger, cursor: 'pointer', touchAction: 'manipulation',
          }}
        >
          {busy ? <Spinner /> : <Mic size={10} />}
          {capState === 'recording' ? '●  Recording' : 'Recording…'}
        </button>
        <KFrames />
      </>
    );
  }

  // 2. The OTHER side is asking — show accept / decline inline.
  if (requestedByThem) {
    return (
      <>
        <span
          data-testid={testId ? `${testId}-prompt` : undefined}
          data-state="requested-by-them"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '2px 4px 2px 8px', borderRadius: 999,
            background: 'rgba(196,90,59,0.14)', border: `1px solid ${OL.clay}`,
            fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            color: OL.cream,
          }}
        >
          <Mic size={10} />
          {partnerLabel} wants to record
          <button
            onClick={acceptRecord}
            disabled={busy}
            data-testid={testId ? `${testId}-accept` : undefined}
            title="Accept recording"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              padding: '2px 7px', borderRadius: 999, border: 0,
              background: OL.stable, color: '#fff', cursor: 'pointer',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            }}
          >
            {busy ? <Spinner /> : <Check size={10} />} Accept
          </button>
          <button
            onClick={declineRecord}
            disabled={busy}
            data-testid={testId ? `${testId}-decline` : undefined}
            title="Decline recording"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              padding: '2px 7px', borderRadius: 999, border: `1px solid ${OL.border2}`,
              background: 'transparent', color: OL.textDim, cursor: 'pointer',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            }}
          >
            <X size={10} /> Decline
          </button>
        </span>
        <KFrames />
      </>
    );
  }

  // 3. I'M asking — waiting on the other side.
  if (requestedByMe) {
    return (
      <>
        <button
          onClick={cancelRequest}
          data-testid={testId}
          data-state="requested-by-me"
          disabled={busy}
          title="Waiting for consent — tap to cancel"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 999,
            background: OL.surface2, border: `1px dashed ${OL.clay}`,
            color: OL.clay, fontSize: 10, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: 'pointer', touchAction: 'manipulation',
            animation: 'pulse-mic 1.6s ease-in-out infinite',
          }}
        >
          {busy ? <Spinner /> : <Mic size={10} />}
          Awaiting consent
        </button>
        <KFrames />
      </>
    );
  }

  // 4. Idle — tap to request.
  return (
    <>
      <button
        onClick={requestRecord}
        data-testid={testId}
        data-state="idle"
        disabled={busy || !myField || capState === 'denied' || capState === 'unsupported'}
        title={error || 'Request voice recording (both players must consent)'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 8px', borderRadius: 999,
          background: OL.surface2, border: `1px solid ${OL.border2}`,
          color: OL.textDim, fontSize: 10, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          cursor: 'pointer', touchAction: 'manipulation',
          opacity: (!myField || capState === 'denied' || capState === 'unsupported' || busy) ? 0.5 : 1,
        }}
      >
        {busy ? <Spinner /> : <MicOff size={10} />}
        Request record
      </button>
      <KFrames />
    </>
  );
};

const KFrames = () => (
  <style>{`
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse-mic {
      0%, 100% { box-shadow: 0 0 0 0 rgba(196,90,59,0.35); }
      50%      { box-shadow: 0 0 0 6px rgba(196,90,59,0.00); }
    }
  `}</style>
);

export default VoiceCaptureControl;
